import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  Plus,
  Trash2,
  Save,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type ValuationItem,
  type ValuationCategoryRecord,
  computeCashPrice,
  computeValuationInsurance,
  computeCategoryTotal,
  parseValue,
  formatGHS,
  getCategoryCode,
} from '@/lib/valuation';

export default function Valuations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['vehicle-valuation-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_valuation_categories')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as ValuationCategoryRecord[];
    },
  });

  // Fetch items
  const { data: dbItems, isLoading: itemsLoading } = useQuery({
    queryKey: ['vehicle-valuations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_valuations')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as ValuationItem[];
    },
  });

  // Local state for editing
  const [items, setItems] = useState<ValuationItem[]>([]);
  const [newItems, setNewItems] = useState<ValuationItem[]>([]);
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync DB data to local state
  useEffect(() => {
    if (dbItems) {
      setItems(dbItems);
      setNewItems([]);
      setHasChanges(false);
    }
  }, [dbItems]);

  // Open all sections by default
  useEffect(() => {
    if (categories) {
      const open: Record<number, boolean> = {};
      categories.forEach((c) => { open[c.id] = true; });
      setOpenSections(open);
    }
  }, [categories]);

  // All items (existing + new)
  const allItems = useMemo(() => [...items, ...newItems], [items, newItems]);

  // Items grouped by category
  const itemsByCategory = useMemo(() => {
    const map = new Map<number, ValuationItem[]>();
    for (const item of allItems) {
      const list = map.get(item.category_id) || [];
      list.push(item);
      map.set(item.category_id, list);
    }
    return map;
  }, [allItems]);

  // Cash price: the cost categories' total less the first-year insurance inside it
  const cashPrice = useMemo(() => {
    if (!categories) return 0;
    return computeCashPrice(allItems, categories);
  }, [allItems, categories]);

  const firstYearInsurance = useMemo(() => {
    if (!categories) return 0;
    return computeValuationInsurance(allItems, categories);
  }, [allItems, categories]);

  // Update an item value
  const updateItem = useCallback((itemId: number | undefined, field: 'key' | 'value', val: string, isNew: boolean) => {
    setHasChanges(true);
    const setter = isNew ? setNewItems : setItems;
    setter((prev) =>
      prev.map((item) =>
        (isNew ? item.sort_order === itemId : item.id === itemId)
          ? { ...item, [field]: val, updated_at: new Date().toISOString() }
          : item
      )
    );
  }, []);

  // Add a new item to a category
  const addItem = useCallback((categoryId: number) => {
    setHasChanges(true);
    const existingItems = itemsByCategory.get(categoryId) || [];
    const maxSort = existingItems.reduce((max, i) => Math.max(max, i.sort_order), -1);
    setNewItems((prev) => [
      ...prev,
      {
        category_id: categoryId,
        key: '',
        value: '0',
        is_default: false,
        sort_order: maxSort + 1,
      },
    ]);
  }, [itemsByCategory]);

  // Remove a new item (only non-default items)
  const removeItem = useCallback((item: ValuationItem, isNew: boolean) => {
    if (item.is_default) return;
    setHasChanges(true);
    if (isNew) {
      setNewItems((prev) => prev.filter((i) => i.sort_order !== item.sort_order || i.category_id !== item.category_id));
    } else {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    }
  }, []);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      // 1. Delete removed non-default items
      const currentIds = new Set(items.filter(i => i.id).map(i => i.id!));
      const dbIds = new Set((dbItems || []).map(i => i.id));
      const deletedIds = [...dbIds].filter(id => {
        const dbItem = dbItems!.find(i => i.id === id);
        return !currentIds.has(id) && dbItem && !dbItem.is_default;
      });

      if (deletedIds.length > 0) {
        const { error } = await supabase
          .from('vehicle_valuations')
          .delete()
          .in('id', deletedIds);
        if (error) throw error;
      }

      // 2. Upsert existing items (update values)
      for (const item of items) {
        if (item.id) {
          const { error } = await supabase
            .from('vehicle_valuations')
            .update({
              key: item.key,
              value: item.value,
              sort_order: item.sort_order,
              updated_at: new Date().toISOString(),
            })
            .eq('id', item.id);
          if (error) throw error;
        }
      }

      // 3. Insert new items
      if (newItems.length > 0) {
        const { error } = await supabase
          .from('vehicle_valuations')
          .insert(
            newItems.map((item) => ({
              category_id: item.category_id,
              key: item.key,
              value: item.value,
              is_default: false,
              sort_order: item.sort_order,
            }))
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-valuations'] });
      toast({ title: 'Saved', description: 'Valuation updated successfully.' });
      setHasChanges(false);
    },
    onError: (error) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    },
  });

  const isLoading = categoriesLoading || itemsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2">Vehicle Valuation</h1>
          <p className="text-muted-foreground">Cost breakdown for vehicle acquisition and preparation.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Cash price badge */}
          <div className="rounded-lg bg-primary/10 border border-primary/20 px-4 py-2 text-right">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cash price</p>
            <p className="text-2xl font-bold text-primary">{formatGHS(cashPrice)}</p>
            {firstYearInsurance > 0 && (
              <p className="text-xs text-muted-foreground">excludes first-year insurance {formatGHS(firstYearInsurance)}</p>
            )}
          </div>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Category sections */}
      <div className="space-y-3">
        {categories?.map((category) => {
          const code = getCategoryCode(category.name);
          const catItems = itemsByCategory.get(category.id) || [];
          const catTotal = computeCategoryTotal(catItems);
          const isOpen = openSections[category.id] ?? true;
          const isSummary = code === 'config';

          return (
            <Card key={category.id}>
              <Collapsible open={isOpen} onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, [category.id]: open }))}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <ChevronDown className={cn('h-4 w-4 transition-transform', !isOpen && '-rotate-90')} />
                        {category.name}
                      </CardTitle>
                      {!isSummary && (
                        <span className="text-sm font-semibold text-muted-foreground tabular-nums">
                          {formatGHS(catTotal)}
                        </span>
                      )}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-3">
                    <div className="space-y-2">
                      {catItems.map((item) => {
                        const isNew = !item.id;
                        return (
                          <div key={isNew ? `new-${item.category_id}-${item.sort_order}` : item.id} className="flex items-center gap-2">
                            <Input
                              value={item.key}
                              onChange={(e) => updateItem(isNew ? item.sort_order : item.id, 'key', e.target.value, isNew)}
                              placeholder="Item name"
                              className="flex-1 h-9 text-sm"
                              disabled={item.is_default}
                            />
                            <Input
                              value={item.value}
                              onChange={(e) => updateItem(isNew ? item.sort_order : item.id, 'value', e.target.value, isNew)}
                              placeholder="0"
                              className="w-32 h-9 text-sm text-right tabular-nums"
                              type="text"
                              inputMode="numeric"
                            />
                            {!item.is_default && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0"
                                onClick={() => removeItem(item, isNew)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            {item.is_default && <div className="w-9 shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                    {/* Add item button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-muted-foreground"
                      onClick={() => addItem(category.id)}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add item
                    </Button>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
